import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { handleError } from "@/api/error";
import { suggestRisks } from "@/lib/assurance/suggest-risks";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// Risk catalog shape is defined in (and the single source of truth from) the API.
export type { MerchantRiskCatalogItem, MerchantRiskCatalogResponse } from "@unbox/api";

const merchantRisksKey = (merchantId: string, workspaceId: string | null) => [
  "merchant-risks",
  merchantId,
  workspaceId,
];

const merchantRiskCatalogKey = (merchantId: string, workspaceId: string | null) => [
  "merchant-risk-catalog",
  merchantId,
  workspaceId,
];

// The catalog of risks the merchant can configure: every global (non-custom)
// risk plus the custom risks already enabled for this merchant, each with whether the merchant
// currently monitors it (`enabled`).
export const useMerchantRiskCatalog = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: merchantRiskCatalogKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].risks.catalog.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].risks.catalog.$get({
            param: scope.param,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export const useMerchantRisksList = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: merchantRisksKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].risks.$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].risks.$get({
            param: scope.param,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

// A single risk's safety configuration: enabled/disabled state is expressed by
// which bucket it lands in; severity is persisted as tier.
// Only merchant-editable keyword config is sent; other detection config remains
// in the admin config flow.
type SafetyRiskConfigEntry = {
  riskId: string;
  tier?: number;
  autoConfirm?: boolean;
  safetyPolicyId?: string | null;
  // Keyword (stringArray) config fields only, e.g. { banned_keywords: [...] }.
  config?: Record<string, string[]>;
  // Fills {creator_risk_explanation} in creator messages when this risk is confirmed.
  customCreatorRiskExplanation?: string | null;
};

const domainKnowledgeKey = (merchantId: string, workspaceId: string | null) => [
  "merchant-domain-knowledge",
  merchantId,
  workspaceId,
];

// The merchant's domain knowledge map (placeholder keyword lists are stored as
// JSON array strings). Used by the safety config editor to prefill placeholder inputs.
export const useMerchantDomainKnowledge = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: domainKnowledgeKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].risks["domain-knowledge"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"].risks["domain-knowledge"].$get({
            param: scope.param,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

// Persists the merchant-facing safety config via the dedicated
// `/risks/safety-config` endpoint (distinct from the admin `/risks/config`
// endpoint, which also edits detection config).
export const useSaveSafetyConfig = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      enabled: SafetyRiskConfigEntry[];
      updated: SafetyRiskConfigEntry[];
      disabled: string[];
      domainKnowledge?: Record<string, string>;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].risks["safety-config"].$post({
            param: scope.param,
            json: input,
          })
        : await client.merchant[":merchantId"].risks["safety-config"].$post({
            param: scope.param,
            json: input,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantRisksKey(scope.merchantId, scope.workspaceId) });
      queryClient.invalidateQueries({ queryKey: merchantRiskCatalogKey(scope.merchantId, scope.workspaceId) });
      queryClient.invalidateQueries({ queryKey: domainKnowledgeKey(scope.merchantId, scope.workspaceId) });
    },
  });
};

export type { RiskSuggestion } from "@/lib/assurance/suggest-risks";

// Catalog risks that match a reviewer's description of what the AI missed, best first.
export const useRiskSuggestions = (description: string) => {
  const { data: catalog } = useMerchantRiskCatalog();
  return useMemo(() => suggestRisks(description, catalog ?? []), [description, catalog]);
};
